<i18n src="./resources/locales.json"></i18n>

<template>
  <div ref="contact" class="contact-list" :class="{ expanded }">
    <div
      class="card-header"
      @contextmenu.prevent="!expanded && openListMenu($event)"
      @click="$parent.toggleExpandedView()"
    >
      <div class="card-title">
        <div class="list-name" v-once>{{ list.name }}</div>
        <div class="contact-count">
          <v-icon small>mdi-account-group</v-icon>
          {{ list.totalContacts }} Contacts
        </div>
      </div>
      <div v-if="expanded" class="menu-action">
        <v-menu bottom left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item v-for="(list, i) in listActions" :key="i" @click="list.action">
              <v-list-item-title>{{ list.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <v-progress-linear
      v-if="isLoading && !expanded"
      indeterminate
      height="3"
      color="var(--v-surface-base)"
      style="margin-top: -3px"
    />

    <v-divider v-show="expanded"></v-divider>

    <div>
      <v-expand-transition>
        <div v-show="expanded">
          <v-tabs v-model="tab" height="40" background-color="transparent">
            <v-tab>Contacts</v-tab>
            <v-tab>Details</v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab" class="tab-content">
            <v-tab-item>
              <div class="search-bar" v-if="!isLoadingContacts">
                <v-text-field
                  v-model="searchString"
                  prepend-inner-icon="mdi-magnify"
                  single-line
                  hide-details
                  solo
                  flat
                  placeholder="Search contacts"
                  height="40"
                  class="search-contacts-field"
                  @input="search"
                />
              </div>
              <v-divider></v-divider>
              <v-progress-linear v-if="isLoadingContacts" height="3" color="primary" indeterminate />
              <v-list class="pa-0">
                <v-virtual-scroll :items="filteredContacts" :item-height="56" :bench="1" :height="308">
                  <template v-slot="{ item }">
                    <v-list-item @click="() => {}">
                      <v-list-item-avatar>
                        <EcsAvatar :contact-id="item.id" :alt="item.displayName" :size="40" />
                      </v-list-item-avatar>
                      <v-list-item-title>{{ item.displayName }}</v-list-item-title>
                      <v-list-item-action>
                        <v-btn icon @click.stop="openContactMenu($event, item)" style="position: relative">
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </template>
                </v-virtual-scroll>
              </v-list>
            </v-tab-item>
            <v-tab-item>
              <v-list two-line color="transparent">
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon color="#fff2">
                      mdi-pencil
                    </v-icon>
                  </v-list-item-icon>

                  <v-list-item-content>
                    <v-list-item-title>{{ list.creator }}</v-list-item-title>
                    <v-list-item-subtitle v-html="modifiedTime"></v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </v-expand-transition>
    </div>
  </div>
</template>

<script>
import { CrmService } from '@/services';
import Contact from '@/classes/Contact';
import EcsAvatar from '@/components/ecs-avatar/EcsAvatar';
import DateTime from 'luxon/src/datetime';
import { mapActions } from 'vuex';

export default {
  name: 'EcsContactListCard',

  components: { EcsAvatar },

  props: {
    expanded: {
      type: Boolean,
      default: false
    },
    list: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      tab: null,
      contacts: [],
      filteredContacts: [],
      isLoading: false,
      isLoadingContacts: false,
      searchString: '',
      listActions: [
        {
          label: 'Add contacts',
          action: async () => {
            if (this.contacts.length === 0) {
              await this.loadListContacts();
            }
            this.$root.$emit('open-contact-selection-dialog', {
              title: this.$t('actions.contactSelection.title', { listName: this.list.name }),
              disabledContacts: this.contacts.map(contact => contact.id),
              action: this.addContactsToList
            });
          }
        },
        {
          label: 'Delete',
          action: () => {
            this.$root.$emit('open-confirm-dialog', {
              title: this.$t('actions.delete.confirm.title'),
              message: this.$t('actions.delete.confirm.message', { name: this.list.name }),
              action: this.deleteList
            });
          }
        }
      ]
    };
  },

  watch: {
    expanded(isExpanded) {
      if (isExpanded) {
        this.loadListContacts();
      } else {
        this.clearListContacts();
      }
    }
  },

  computed: {
    modifiedTime() {
      const time = DateTime.fromISO(this.list.createTime);
      return `${time.toLocaleString(DateTime.DATETIME_FULL)} &bull; ${time
        .toRelative()
        .replace(/^\\w/, c => c.toUpperCase())}`;
    }
  },

  created() {},

  methods: {
    ...mapActions(['removeList', 'searchLists', 'snackbarSuccessMessage', 'snackbarErrorMessage']),

    loadListContacts() {
      this.isLoading = true;
      this.isLoadingContacts = true;
      this.contacts = [];
      this.filteredContacts = [];
      return CrmService.getListContacts(this.list.id)
        .then(({ data }) => {
          this.contacts = data.contacts.map(contact => new Contact(contact));
          this.search();
        })
        .finally(() => {
          this.isLoading = false;
          this.isLoadingContacts = false;
        });
    },

    clearListContacts() {
      this.contacts = [];
      this.filteredContacts = [];
    },

    formatNoteDate(date) {
      return DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_FULL);
    },

    search() {
      this.filteredContacts = this.contacts.filter(contact => contact.includes(this.searchString));
    },

    deleteList(confirmed) {
      if (confirmed) {
        return CrmService.deleteList(this.list.id).then(() => {
          this.snackbarSuccessMessage(this.$t('actions.delete.success', { name: this.list.name }));
          this.removeList(this.list.id);
        });
      } else {
        return Promise.resolve();
      }
    },

    addContactsToList(contacts) {
      const assignments = [];
      contacts.forEach(contactId => assignments.push(CrmService.assignContactToList(contactId, this.list.id)));

      return Promise.all(assignments).then(responses => {
        const n = responses.reduce((prev, curr) => {
          prev += curr.status === 200 ? 1 : 0;
          return prev;
        }, 0);
        this.snackbarSuccessMessage(this.$t('actions.add.success', { n, listName: this.list.name }));
        if (n < contacts.length) {
          this.snackbarErrorMessage(this.$t('actions.add.error', { n: contacts.length - n }));
        }
        this.loadListContacts();
        this.search();
      });
    },

    removeContactFromList(confirmed, contact) {
      if (confirmed) {
        return CrmService.removeContactFromList(contact.id, this.list.id).then(() => {
          this.snackbarSuccessMessage(
            this.$t('contact.menu.remove.success', { contactName: contact.displayName, listName: this.list.name })
          );
          this.loadListContacts();
          this.search();
        });
      } else {
        return Promise.resolve();
      }
    },

    openListMenu(event) {
      this.$root.$emit('open-context-menu', {
        menu: this.listActions,
        position: {
          x: event.clientX,
          y: event.clientY
        }
      });
    },

    createListContactActions(contact) {
      return [
        {
          label: this.$t('contact.menu.remove.confirm.title'),
          action: () => {
            this.$root.$emit('open-confirm-dialog', {
              title: this.$t('contact.menu.remove.confirm.title'),
              message: this.$t('contact.menu.remove.confirm.message', {
                contactName: contact.displayName,
                listName: this.list.name
              }),
              action: confirmed => this.removeContactFromList(confirmed, contact)
            });
          }
        }
      ];
    },

    openContactMenu(event, contact) {
      this.$root.$emit('open-context-menu', {
        menu: this.createListContactActions(contact),
        position: {
          x: event.clientX,
          y: event.clientY
        }
      });
    }
  }
};
</script>

<style scoped src="./scoped.scss" lang="scss" />
<style lang="scss">
.search-contacts-field .v-input__control {
  min-height: 40px !important;
  font-size: 15px;
}
</style>
